'use client';

// Bridges the room editor's live analysis state to the auralization engine.
//
// No new network calls: the before/after curves are already in `analysis.freq`
// and `analysis.rt60` (the charts render them). Those slots are the live
// editor analysis — `applyStaleClass` nulls them on geometry/panel edits and
// `ANALYSIS_DONE` rewrites them — so a rebuild here always hears the latest
// Compute, including after a Design (`iterations.activeId`) switch.
//
// Before = the bare room. After = the panelled room, and only then.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AuralizationEngine, type TrimWindow } from '@/lib/audio/engine';
import type { RoomAcoustics } from '@/lib/audio/types';
import type { FreqResponseResponse, RT60Response, RT60Fine } from '@/lib/adminTypes';

export type AbState = 'before' | 'after';

interface Params {
  freq: FreqResponseResponse | null;
  rt60: RT60Response | null;
  /** Active placed-panel count → the treated state's direct/reverberant target. */
  nProducts: number;
  /** Gate: only build/convolve when the feature is unlocked. */
  enabled: boolean;
}

export interface AuralizationApi {
  status: string;
  building: boolean;
  /** A source is being prepared (fetch → decode → convolve) and isn't playable
   *  yet. Distinct from `building` (a churning status string): this stays true
   *  for the whole load, so the UI can show one clean loading state and disable
   *  play until the freshly-picked source is ready. */
  loading: boolean;
  /** A persistent source-load failure (missing sample file / undecodable upload),
   *  independent of the churning `status` line. Cleared on a new load or success. */
  error: string | null;
  hasData: boolean;
  ready: AbState[];
  isPlaying: boolean;
  activeState: AbState;
  elapsed: number;
  duration: number;
  sourceName: string | null;
  loadFile: (file: File) => void;
  loadSampleUrl: (url: string, name: string, trim?: TrimWindow) => void;
  togglePlay: () => void;
  setActive: (s: string) => void;
  seekTo: (seconds: number) => void;
}

function toRoom(
  freqs: number[] | undefined,
  mag: number[] | undefined,
  fine: RT60Fine | undefined,
  bands: Record<string, number> | undefined,
  nProducts: number,
): RoomAcoustics | null {
  if (!freqs?.length || !mag?.length || !fine?.freqs?.length || !bands) return null;
  return { freqResponse: { freqs, mag }, rt60Fine: fine, rt60Bands: bands, nProducts };
}

/** Untreated = `mag` when the scene has no panels; `mag_baseline` once a
 *  treated solve is on the wire. After is omitted until there are panels. */
export function buildStates(
  freq: FreqResponseResponse | null,
  rt60: RT60Response | null,
  nProducts: number,
): Record<string, RoomAcoustics> | null {
  if (!freq || !rt60) return null;
  const treated = nProducts > 0;
  const before = treated
    ? toRoom(freq.freqs, freq.mag_baseline, rt60.rt60_fine_baseline, rt60.rt60_baseline, 0)
    : toRoom(freq.freqs, freq.mag, rt60.rt60_fine, rt60.rt60, 0);
  const after = treated
    ? toRoom(freq.freqs, freq.mag, rt60.rt60_fine, rt60.rt60, nProducts)
    : null;
  if (!before && !after) return null;
  const states: Record<string, RoomAcoustics> = {};
  if (before) states.before = before;
  if (after) states.after = after;
  return states;
}

const isBuildingStatus = (s: string) => /^(Building|Processing|Decoding)/.test(s);

export function useAuralization({ freq, rt60, nProducts, enabled }: Params): AuralizationApi {
  const engineRef = useRef<AuralizationEngine | null>(null);
  const [status, setStatus] = useState('');
  const [ready, setReady] = useState<AbState[]>([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [activeState, setActiveStateS] = useState<AbState>('before');
  const [elapsed, setElapsed] = useState(0);
  const [duration, setDuration] = useState(0);
  const [sourceName, setSourceName] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [armed, setArmed] = useState(false);
  const [loading, setLoading] = useState(false);

  // One engine for the component's lifetime.
  useEffect(() => {
    const eng = new AuralizationEngine({
      onStatus: setStatus,
      onReady: (states) => {
        // A successful convolution clears any earlier load failure and ends the
        // load — the picked source is now playable.
        setLoadError(null);
        setLoading(false);
        setReady(states.filter((s): s is AbState => s === 'before' || s === 'after'));
      },
      onPlayingChange: setIsPlaying,
      onProgress: (e, d) => { setElapsed(e); setDuration(d); },
      onEnded: () => setElapsed(0),
    });
    engineRef.current = eng;
    return () => { eng.dispose(); engineRef.current = null; };
  }, []);

  // Cheap signature so we only rebuild when the curves actually change — the
  // auto-sim hands us a fresh object identity on every tick.
  const sig = useMemo(() => {
    if (!freq || !rt60) return '';
    const m = freq.mag ?? [];
    const b = freq.mag_baseline ?? [];
    const head = `${freq.freqs?.length ?? 0}:${m.length}:${m[0] ?? ''}:${m[m.length - 1] ?? ''}`;
    const base = `${b.length}:${b[0] ?? ''}:${b[b.length - 1] ?? ''}`;
    return `${head}|${base}|${Object.keys(rt60.rt60 ?? {}).length}|${rt60.rt60_fine?.rt60?.length ?? 0}|${nProducts}`;
  }, [freq, rt60, nProducts]);

  // (Re)build IRs once the user has engaged (armed) and we're unlocked.
  // Debounced so dragging a panel around doesn't thrash the synthesizer.
  useEffect(() => {
    if (!armed || !enabled || !sig) return;
    const eng = engineRef.current;
    if (!eng) return;
    const states = buildStates(freq, rt60, nProducts);
    // No usable curves yet — the panel renders its own "Run Compute" guidance
    // via `hasData`, so just bail (don't setState synchronously in an effect).
    if (!states) return;
    const t = setTimeout(() => { void eng.setStates(states); }, 400);
    return () => clearTimeout(t);
    // freq/rt60/nProducts are folded into `sig`; depending on the raw objects
    // would rebuild on every auto-sim tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [armed, enabled, sig]);

  const loadFile = useCallback((file: File) => {
    setLoadError(null);
    setArmed(true);
    setSourceName(file.name);
    // New source: the previous convolutions are stale, so disable play and show
    // the load state until onReady re-populates them. resume() unlocks the
    // context inside this click gesture.
    setReady([]);
    setLoading(true);
    const eng = engineRef.current;
    eng?.resume();
    eng?.loadSourceFile(file).catch(() => {
      setLoading(false);
      setLoadError('Could not decode that file — is it a valid audio file?');
    });
  }, []);

  const loadSampleUrl = useCallback((url: string, name: string, trim?: TrimWindow) => {
    setLoadError(null);
    setArmed(true);
    setSourceName(name);
    setReady([]);
    setLoading(true);
    const fileName = url.split('/').pop() ?? url;
    const eng = engineRef.current;
    eng?.resume();
    eng?.loadSourceUrl(url, trim).catch(() => {
      setLoading(false);
      setLoadError(`"${name}" isn't uploaded yet — add ${fileName} to public/audio/samples/.`);
    });
  }, []);

  // After is not a real state until panels exist — stay on before so Play
  // cannot fall through to the dry source (engine.currentBuffer fallback).
  const resolvedActive: AbState = nProducts > 0 ? activeState : 'before';

  const togglePlay = useCallback(() => { engineRef.current?.toggle(resolvedActive); }, [resolvedActive]);

  const setActive = useCallback((s: string) => {
    if (s !== 'before' && s !== 'after') return;
    if (s === 'after' && nProducts === 0) return;
    setActiveStateS(s);
    engineRef.current?.setActiveState(s);
  }, [nProducts]);

  const seekTo = useCallback((seconds: number) => { engineRef.current?.seek(seconds); }, []);

  return {
    status,
    building: isBuildingStatus(status),
    loading,
    error: loadError,
    hasData: !!(freq && rt60),
    ready,
    isPlaying,
    activeState: resolvedActive,
    elapsed,
    duration,
    sourceName,
    loadFile,
    loadSampleUrl,
    togglePlay,
    setActive,
    seekTo,
  };
}
